import { fontSizes } from "../sizes";

const chakraBaseStyle = {
  fontWeight: "semibold",
};

const baseStyle = {
  ...chakraBaseStyle,
  color: "fg-main",
  fontFamily: "Moderat",
  fontWeight: "bold", // CUI: 700
};

// Currently, these are all overwritten in the `sizes` object below
const chakraSizes = {
  "2xl": {
    fontSize: "7xl",
    lineHeight: "5.625rem",
    letterSpacing: "tight",
  },
  xl: {
    fontSize: "6xl",
    lineHeight: "4.5rem",
    letterSpacing: "tight",
  },
  lg: {
    fontSize: "5xl",
    lineHeight: "3.75rem",
    letterSpacing: "tight",
  },
  md: {
    fontSize: "4xl",
    lineHeight: "2.75rem",
    letterSpacing: "tight",
  },
  sm: {
    fontSize: "3xl",
    lineHeight: "2.375rem",
  },
  xs: {
    fontSize: "2xl",
    lineHeight: "2rem",
  },
};

// default size is `xl`
// Used: xs, md, xl, 2xl
const sizes = {
  ...chakraSizes,
  "2xl": {
    fontSize: [fontSizes.x2l, fontSizes.x2l, fontSizes.x3l], // CUI: ["2.25rem", "2.25rem", "3rem"]
    lineHeight: ["2.66rem", "2.66rem", "3rem"], // CUI: ["2.77rem", "2.77rem", "3rem"]
  },
  xl: { fontSize: [fontSizes.md, fontSizes.md, fontSizes.x2l] }, // CUI: ["1.875rem", "1.875rem", "2.25rem"]
  lg: { fontSize: [fontSizes.sm, fontSizes.sm, fontSizes.lg] }, // CUI: ["1.5rem", "1.5rem", "1.875rem"]
  md: { fontSize: fontSizes.lg }, // From CUI
  sm: { fontSize: fontSizes.md }, // From CUI
  xs: { fontSize: fontSizes.sm }, // From CUI
};

interface ThFont {
  fontFamily: string;
  fontWeight: string;
  fontSize: string;
  lineHeight: string;
  letterSpacing: string;
}

const th: Record<string, ThFont> = {
  h1: {
    fontFamily: "Moderat Extended",
    fontWeight: "black",
    fontSize: "3.5rem",
    lineHeight: "110%",
    letterSpacing: "0",
  },
  h1m: {
    fontFamily: "Moderat Extended",
    fontWeight: "black",
    fontSize: "2rem",
    lineHeight: "120%",
    letterSpacing: "0",
  },
  h2: {
    fontFamily: "Moderat Extended",
    fontWeight: "bold",
    fontSize: "2.75rem",
    lineHeight: "110%",
    letterSpacing: "0",
  },
  h2m: {
    fontFamily: "Moderat Extended",
    fontWeight: "bold",
    fontSize: "1.5rem",
    lineHeight: "120%",
    letterSpacing: "0",
  },
  h3: {
    fontFamily: "Moderat Extended",
    fontWeight: "bold",
    fontSize: "2rem",
    lineHeight: "120%",
    letterSpacing: "0",
  },
  h3m: {
    fontFamily: "Moderat Extended",
    fontWeight: "bold",
    fontSize: "22px",
    lineHeight: "120%",
    letterSpacing: "0",
  },
  h4: {
    fontFamily: "Moderat",
    fontWeight: "bold",
    fontSize: "1.5rem",
    lineHeight: "120%",
    letterSpacing: "0",
  },
  h4m: {
    fontFamily: "Moderat",
    fontWeight: "bold",
    fontSize: "18px",
    lineHeight: "120%",
    letterSpacing: "0",
  },
  h5: {
    fontFamily: "Moderat",
    fontWeight: "normal",
    fontSize: "1.5rem",
    lineHeight: "120%",
    letterSpacing: "0",
  },
  h5m: {
    fontFamily: "Moderat",
    fontWeight: "normal",
    fontSize: "18px",
    lineHeight: "120%",
    letterSpacing: "0",
  },
  h6: {
    fontFamily: "Moderat",
    fontWeight: "bold",
    fontSize: "22px",
    lineHeight: "120%",
    letterSpacing: "0",
  },
  h6m: {
    fontFamily: "Moderat",
    fontWeight: "bold",
    fontSize: "1rem",
    lineHeight: "120%",
    letterSpacing: "0",
  },
};

interface ThVariant {
  fontFamily: string[];
  fontWeight: string[];
  fontSize: string[];
  lineHeight: string[];
  letterSpacing: string[];
}

type Prop = keyof ThVariant;

const toMobile = (desktop: ThFont, mobile: ThFont, prop: Prop): string[] => [
  mobile[prop],
  mobile[prop],
  desktop[prop],
  desktop[prop],
];

const shrinkForMobile = (desktop: ThFont, mobile: ThFont): ThVariant => ({
  fontFamily: toMobile(desktop, mobile, "fontFamily"),
  fontWeight: toMobile(desktop, mobile, "fontWeight"),
  fontSize: toMobile(desktop, mobile, "fontSize"),
  lineHeight: toMobile(desktop, mobile, "lineHeight"),
  letterSpacing: toMobile(desktop, mobile, "letterSpacing"),
});

const variants = {
  "th-h1-default": {
    as: "h1",
    color: "fg-accent",
    fontFamily: "Moderat Extended",
    fontSize: ["1.5rem", "1.5rem", "3.5rem"],
    fontWeight: "black",
    lineHeight: ["120%", "120%", "110%"],
    letterSpacing: "0",
  },
  "h1-default": {
    as: "h1",
    color: "fg-accent",
    fontSize: ["2.25rem", "2.25rem", "2.75rem"],
    fontWeight: "black",
    lineHeight: "133%",
  },
  "h2-default": {
    as: "h2",
    fontSize: ["1.5rem", "1.5rem", "2rem"],
    fontWeight: "black",
    lineHeight: "shorter",
  },
  "h3-default": {
    as: "h3",
    fontSize: ["1.25rem", "1.25rem", "1.5rem"],
    fontWeight: "black",
    lineHeight: "133%",
  },
  "h4-default": {
    as: "h4",
    fontSize: ["1rem", "1rem", "1.25rem"],
    fontWeight: "black",
    lineHeight: "133%",
  },
  "th-h1": shrinkForMobile(th.h1, th.h1m),
  "th-h2": shrinkForMobile(th.h2, th.h2m),
  "th-h3": shrinkForMobile(th.h3, th.h3m),
  "th-h4": shrinkForMobile(th.h4, th.h4m),
  "th-h5": shrinkForMobile(th.h5, th.h5m),
  "th-h6": shrinkForMobile(th.h6, th.h6m),
};

export const Heading = {
  baseStyle,
  sizes,
  variants,
};
